import { z } from 'zod';
import { centsSchema, idSchema, isoDateSchema, positiveCentsSchema, timestampSchema } from './common.js';
export const PROPERTY_KINDS = ['apartment', 'house', 'parking', 'land', 'other'];
export const PROPERTY_USAGES = [
    'long-term-rental',
    'short-term-rental',
    'vacant',
    'personal',
];
const nullableText = (max) => z.string().trim().min(1).max(max).nullable();
/** Champs d'un bien tels qu'ils sont saisis. Les facultatifs valent `null` s'ils sont omis. */
export const propertyInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum(PROPERTY_KINDS),
    usage: z.enum(PROPERTY_USAGES),
    /** ISO 3166-1 alpha-2 : `FR`, `PT`… */
    country: z.string().regex(/^[A-Z]{2}$/, 'Code pays ISO sur deux lettres majuscules'),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Code devise ISO sur trois lettres majuscules').default('EUR'),
    address: nullableText(200).default(null),
    postalCode: nullableText(20).default(null),
    city: nullableText(120).default(null),
    surfaceM2: z.number().positive().max(100_000).nullable().default(null),
    rooms: z.number().int().min(0).max(100).nullable().default(null),
    /** `null` : bien non acquis (scénario d'étude). */
    acquiredOn: isoDateSchema.nullable().default(null),
    notes: z.string().trim().min(1).max(2000).nullable().default(null),
});
/**
 * Modification : tout est facultatif. Une valeur `null` efface un champ facultatif ;
 * `archived` archive ou rétablit le bien (rien n'est jamais supprimé).
 */
export const propertyUpdateSchema = z
    .object({
    name: propertyInputSchema.shape.name,
    kind: propertyInputSchema.shape.kind,
    usage: propertyInputSchema.shape.usage,
    country: propertyInputSchema.shape.country,
    currency: z.string().regex(/^[A-Z]{3}$/),
    address: nullableText(200),
    postalCode: nullableText(20),
    city: nullableText(120),
    surfaceM2: z.number().positive().max(100_000).nullable(),
    rooms: z.number().int().min(0).max(100).nullable(),
    acquiredOn: isoDateSchema.nullable(),
    notes: z.string().trim().min(1).max(2000).nullable(),
    archived: z.boolean(),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, { message: 'Aucun champ à modifier' });
export const propertySchema = z.object({
    id: idSchema,
    name: z.string(),
    kind: z.enum(PROPERTY_KINDS),
    usage: z.enum(PROPERTY_USAGES),
    country: z.string(),
    currency: z.string(),
    address: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    surfaceM2: z.number().nullable(),
    rooms: z.number().int().nullable(),
    acquiredOn: isoDateSchema.nullable(),
    notes: z.string().nullable(),
    archivedAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
/** Coûts d'acquisition : un seul jeu par bien, remplacé en bloc. Tous en centimes. */
export const propertyCostsInputSchema = z.object({
    /** Prix du bien, frais d'agence inclus. */
    purchasePriceCents: positiveCentsSchema,
    notaryFeesCents: centsSchema.default(0),
    worksCents: centsSchema.default(0),
    planCents: centsSchema.default(0),
    furnitureCents: centsSchema.default(0),
    /** Honoraires. */
    agencyFeesCents: centsSchema.default(0),
    /** Frais de garantie (crédit logement, hypothèque). */
    guaranteeFeesCents: centsSchema.default(0),
});
export const propertyCostsSchema = propertyCostsInputSchema.extend({
    propertyId: idSchema,
    /** Somme des postes : coût total du projet. */
    totalCents: centsSchema,
    updatedAt: timestampSchema,
});
//# sourceMappingURL=property.js.map